import { useEffect, useMemo, useState } from 'react';
import { Modal, TextInput, Stack, Table, Paper, Button, Center, Loader, Text, Group } from '@mantine/core';
import { IconScope as IconSearch } from '../../components/icons/CncIcons';
import { api, imageUrl } from '../../api/client';

interface AccessoryListItem {
  id: number;
  name: string;
  manufacturer: string | null;
  model: string | null;
  imagePath: string | null;
  hourlyRateEur: number | null;
  distributors?: { name: string }[];
}

export default function SelectAccessoryModal({
  equipmentId,
  attachedIds,
  onClose,
  onDone,
}: {
  equipmentId: number;
  attachedIds: number[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [all, setAll] = useState<AccessoryListItem[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      const list = await api.get<AccessoryListItem[]>('/accessories');
      const withDist = await Promise.all(list.map(async (c) => {
        try {
          const full = await api.get<{ distributors: { name: string }[] }>(`/accessories/${c.id}`);
          return { ...c, distributors: full.distributors };
        } catch {
          return { ...c, distributors: [] };
        }
      }));
      setAll(withDist);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((a) =>
      a.name.toLowerCase().includes(q)
      || (a.manufacturer ?? '').toLowerCase().includes(q)
      || (a.distributors ?? []).some((d) => d.name.toLowerCase().includes(q))
    );
  }, [all, search]);

  async function attach(accId: number) {
    setBusyId(accId);
    try {
      await api.post(`/equipment/${equipmentId}/accessories/${accId}`);
      onDone();
    } finally { setBusyId(null); }
  }

  return (
    <Modal opened onClose={onClose} title="Select Accessory" size="lg">
      <Stack>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search by accessory, manufacturer, or distributor…"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          autoFocus
        />
        {loading ? (
          <Center p="xl"><Loader /></Center>
        ) : (
          <Paper withBorder style={{ maxHeight: 400, overflowY: 'auto' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: 60 }}>Image</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Manufacturer</Table.Th>
                  <Table.Th>Hourly rate</Table.Th>
                  <Table.Th style={{ width: 100 }}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((a) => {
                  const attached = attachedIds.includes(a.id);
                  return (
                    <Table.Tr key={a.id}>
                      <Table.Td>
                        <div className="image-box">{a.imagePath && <img src={imageUrl(a.imagePath, true)} alt={a.name} />}</div>
                      </Table.Td>
                      <Table.Td>{a.name}</Table.Td>
                      <Table.Td>{a.manufacturer ?? '—'}</Table.Td>
                      <Table.Td>{a.hourlyRateEur != null ? `€ ${a.hourlyRateEur.toFixed(2)}/h` : '—'}</Table.Td>
                      <Table.Td>
                        <Button size="xs" disabled={attached} loading={busyId === a.id} onClick={() => attach(a.id)}>
                          {attached ? 'Added' : 'Add'}
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
                {filtered.length === 0 && (
                  <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" ta="center" size="sm">No matches.</Text></Table.Td></Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
